import { defineStore } from 'pinia'
import { ref } from 'vue'
import log from '@/utils/useLogger'

/**
 * FLY3D-612 — Live deploy-progress state, keyed by `dock_id`.
 *
 * The single source of truth for any UI surface that needs to
 * reflect a running dock deploy: the onboarding wizard's Deploy
 * step, the device-list pair button text, the deployDockModal,
 * and any future dashboard chrome.
 *
 * IFB-65 polls n8n state every 2s and pushes the full payload to
 * IFB-64, which fans out over WebSocket. `useDeployProgressSocket`
 * owns the connection and writes payloads here via
 * `applyPayload(dockId, payload)`. Consumers read reactively.
 *
 * Step → stage mapping (DeployPanel's 5 abstract stages collapse
 * the backend's 12 concrete steps into something stable to render):
 *   stage 0 — Verifying device entitlement
 *     deploy_started, ecr_authenticated
 *   stage 1 — Packaging IDI-API
 *     repo_cloned, versions_resolved, config_updated
 *   stage 2 — Uploading & installing
 *     services_stopped, images_pulled, services_started
 *   stage 3 — Post-install health checks
 *     redis_healthy, redis_seeded
 *   stage 4 — Deploy complete
 *     deploy_complete
 *   (deploy_failed keeps the last stage index, flips status='failed')
 *
 * `currentStage` matches DeployPanel's prop semantics: it's the
 * number of stages already DONE — so the stage at that index is
 * the one currently in progress, and stages below it are checked.
 */

export type DeployStatus = 'idle' | 'connecting' | 'live' | 'success' | 'failed' | 'error'

export interface DeployStepEntry {
  step: string
  message: string
  timestamp: string
  status?: string
}

export interface DeployResult {
  status?: 'success' | 'failure' | string
  dock_type?: string
  machine?: string
  deployed_at?: string
  run_id?: string
  versions?: Record<string, string>
  config?: {
    dock_id?: string
    rc_id?: string
    organisation_id?: string
    dji_gateway_sn?: string
    [k: string]: unknown
  }
  mqtt?: { host?: string; port?: number }
  redis_seed?: {
    status?: string
    binding_code?: string
    organisation_id?: string
    devices_seeded?: string[]
  }
  failed_step?: string
  error?: string
  [k: string]: unknown
}

export interface DeployPayload {
  dock_id: string
  run_id?: string
  current_step?: string
  current_message?: string
  status?: 'in_progress' | 'success' | 'failure' | string
  steps?: DeployStepEntry[]
  result?: DeployResult | null
  poll_again?: boolean
}

export interface DeployProgressEntry {
  dockId: string
  status: DeployStatus
  currentStep: string | null
  currentMessage: string | null
  steps: DeployStepEntry[]
  result: DeployResult | null
  runId: string | null
  /** Number of DeployPanel stages already completed (0..5). */
  currentStage: number
  /** Last surfaced error reason for UI. */
  errorReason: string | null
  /** Wall-clock time the first payload arrived — drives the elapsed timer. */
  startedAt: number | null
  /** Last payload's `poll_again` (false ⇒ terminal). */
  pollAgain: boolean
}

const STEP_TO_DONE_COUNT: Record<string, number> = {
  deploy_started: 0,
  ecr_authenticated: 0,
  repo_cloned: 1,
  versions_resolved: 1,
  config_updated: 1,
  services_stopped: 2,
  images_pulled: 2,
  services_started: 2,
  redis_healthy: 3,
  redis_seeded: 3,
  deploy_complete: 5,
}

const blankEntry = (dockId: string): DeployProgressEntry => ({
  dockId,
  status: 'idle',
  currentStep: null,
  currentMessage: null,
  steps: [],
  result: null,
  runId: null,
  currentStage: 0,
  errorReason: null,
  startedAt: null,
  pollAgain: true,
})

export const useDeployProgressStore = defineStore('deployProgress', () => {
  const entries = ref<Record<string, DeployProgressEntry>>({})

  const ensureEntry = (dockId: string): DeployProgressEntry => {
    if (!entries.value[dockId]) {
      entries.value[dockId] = blankEntry(dockId)
    }
    return entries.value[dockId]
  }

  const getEntry = (dockId: string | null | undefined): DeployProgressEntry | null => {
    if (!dockId) return null
    return entries.value[dockId] ?? null
  }

  const setStatus = (dockId: string, status: DeployStatus, errorReason: string | null = null) => {
    const entry = ensureEntry(dockId)
    entry.status = status
    if (errorReason !== null) entry.errorReason = errorReason
  }

  const applyPayload = (payload: DeployPayload) => {
    if (!payload?.dock_id) return
    const entry = ensureEntry(payload.dock_id)

    if (entry.startedAt === null) entry.startedAt = Date.now()
    if (payload.run_id) entry.runId = payload.run_id
    if (payload.current_step) entry.currentStep = payload.current_step
    if (payload.current_message) entry.currentMessage = payload.current_message
    if (Array.isArray(payload.steps) && payload.steps.length > 0) entry.steps = payload.steps
    if (payload.result) entry.result = payload.result
    if (typeof payload.poll_again === 'boolean') entry.pollAgain = payload.poll_again

    // Derive currentStage from the latest known step. `deploy_failed`
    // never advances the stage — it only flips status.
    const step = payload.current_step
    if (step && step !== 'deploy_failed' && step in STEP_TO_DONE_COUNT) {
      const next = STEP_TO_DONE_COUNT[step]
      // Stage index never moves backwards — guards against late-arriving
      // out-of-order payloads (shouldn't happen with the IFB-65 loop, but
      // cheap to defend).
      if (next > entry.currentStage) entry.currentStage = next
    }

    // Status precedence: explicit failure > explicit success > live.
    if (payload.status === 'failure' || step === 'deploy_failed') {
      entry.status = 'failed'
      entry.errorReason = payload.current_message || payload.result?.error || 'Deployment failed.'
    } else if (payload.status === 'success' || step === 'deploy_complete') {
      const isTerminal = payload.poll_again === false
      const hasResult = !!(payload.result || entry.result)
      if (isTerminal && !hasResult) {
        log.info(
          `deployProgress: success-without-result for ${payload.dock_id} — run_id=${payload.run_id ?? entry.runId ?? 'n/a'} step=${step ?? 'n/a'} message=${payload.current_message ?? 'n/a'}`,
        )
        entry.status = 'failed'
        entry.errorReason = 'An error occurred. Please try again.'
      } else {
        entry.status = 'success'
        entry.currentStage = 5
      }
    } else if (entry.status !== 'success' && entry.status !== 'failed') {
      entry.status = 'live'
    }
  }

  const reset = (dockId: string) => {
    entries.value[dockId] = blankEntry(dockId)
  }

  const remove = (dockId: string) => {
    delete entries.value[dockId]
  }

  return {
    entries,
    ensureEntry,
    getEntry,
    setStatus,
    applyPayload,
    reset,
    remove,
  }
})
